import {Component, Input, OnInit, inject, ChangeDetectionStrategy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {VwuiAngularModule} from '@bvgo-ict/vwui-angular';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import * as FileSaver from 'file-saver';

import {ApiService, ProjectFileService, ToastService} from '../../services';
import {ProjectDetails} from '../../models';
import {IAttachment, IPageableAttachments} from '../../models/project/project-data';

const PAGE_SIZE = 5;

/**
 * "Bijlagen" card on the project details page: lists, uploads and downloads
 * the attachments of a project via the paginated project attachments endpoint.
 */
@Component({
    selector: 'app-project-attachments-card',
    templateUrl: './project-attachments-card.component.html',
    styleUrls: ['./project-attachments-card.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, TranslatePipe, VwuiAngularModule]
})
export class ProjectAttachmentsCardComponent implements OnInit {
    private readonly apiService = inject(ApiService);
    private readonly projectFileService = inject(ProjectFileService);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);

    @Input({required: true}) public projectDetails: ProjectDetails;

    public attachments: IAttachment[] = [];
    public page: IPageableAttachments['page'] | null = null;
    public isLoading = false;
    public isUploading = false;
    public isDownloadingAll = false;
    public deletingUuid: string | null = null;

    private readonly supportedExtensions = ['.pdf', '.xls', '.xlsx', '.doc', '.docx', '.ppt', '.pptx'];
    private readonly maxSizeInMb = 100;

    public ngOnInit(): void {
        this.loadAttachments(0);
    }

    public setPage(pageNumber: number): void {
        this.loadAttachments(pageNumber - 1);
    }

    public firstItem(): number {
        return this.page ? (this.page.number * PAGE_SIZE) + 1 : 0;
    }

    public lastItem(): number {
        return this.page ? Math.min((this.page.number + 1) * PAGE_SIZE, this.page.totalElements) : 0;
    }

    public download(attachment: IAttachment): void {
        this.apiService.getAttachment(`/attachment/${attachment.uniqueId}`, attachment.contentType)
            .then((blob) => FileSaver.saveAs(blob, attachment.originalFileName))
            .catch((error) => this.handleError(error, 'ERRORS.attachment_download_failed'));
    }

    public downloadAll(): void {
        this.isDownloadingAll = true;
        this.apiService.getAttachment(`/pdf/attachments/${this.projectDetails.id}`, 'application/zip')
            .then((blob) => FileSaver.saveAs(blob, `${this.projectDetails.name}-bijlagen.zip`))
            .catch((error) => this.handleError(error, 'ERRORS.attachment_download_failed'))
            .finally(() => this.isDownloadingAll = false);
    }

    public deleteAttachment(attachment: IAttachment): void {
        this.deletingUuid = attachment.uniqueId;
        this.projectFileService.deleteDocument(attachment.uniqueId, this.projectDetails.id).subscribe({
            next: () => {
                this.deletingUuid = null;
                this.loadAttachments(this.page?.number ?? 0);
            },
            error: () => {
                this.deletingUuid = null;
            }
        });
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) {
            return;
        }

        if (!this.isSupportedExtension(file)) {
            this.toastService.openError(400, this.translateService.instant('ERRORS.project_document_type_not_allowed'));
            return;
        }

        if (!this.isFileSizeOk(file)) {
            this.toastService.openError(
                400,
                this.translateService.instant('COMPONENT.QUESTION_ATTACHMENT.filesize_error', {maxSizeInMb: this.maxSizeInMb})
            );
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        this.isUploading = true;
        this.projectFileService.uploadDocument(this.projectDetails.id, formData).subscribe({
            next: () => this.loadAttachments(0),
            error: () => {
                this.isUploading = false;
            },
            complete: () => {
                this.isUploading = false;
            }
        });
    }

    public getSupportedExtensions(): string {
        return this.supportedExtensions.join(',');
    }

    public trackAttachment(_index: number, attachment: IAttachment): string {
        return attachment.uniqueId;
    }

    private isSupportedExtension(file: File): boolean {
        const extension = /(?:\.([^.]+))?$/.exec(file.name)[0].toLowerCase();
        return this.supportedExtensions.includes(extension);
    }

    private isFileSizeOk(file: File): boolean {
        return file.size < this.maxSizeInMb * 1024 * 1024;
    }

    private loadAttachments(pageNumber: number): void {
        this.isLoading = true;
        this.projectFileService.listDocuments(this.projectDetails.id, pageNumber, PAGE_SIZE).subscribe({
            next: (response) => {
                this.attachments = response.content;
                this.page = response.page;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    private handleError(error: {status?: number}, translationKey: string): void {
        console.error(error);
        this.toastService.openError(error.status ?? 0, this.translateService.instant(translationKey));
    }
}
